import React, { useState, useEffect } from 'react';
/**
 * SourceAnalysisDashboard
 * ──────────────────────────────────────────────────────────────────
 * Four citation charts powered by GET /api/modules/citations/:campaignId.
 *
 * Chart 2  byCategory        — publisher type breakdown (all-time)
 * Chart 3  byCategoryEngine  — source types per engine (stacked bars)
 * Chart 4  competitorShare   — who gets cited (owned / competitor / third-party)
 * Chart 1  timeSeries        — citation volume over time (per audit run)
 *
 * Pure CSS bars — no charting library dependency.
 * Receives apiBaseUrl and campaignId as props (same pattern as CitationsDashboard).
 */
// ── Publisher category colours — GEO Jetpack brand palette ───────
const CATEGORY_COLORS = {
  'News':       '#e76f51',   // Burnt Sienna
  'Government': '#01796f',   // Pine Green
  'Academic':   '#048ba8',   // Blue Munsell
  'Reference':  '#0091AD',   // Blue Munsell (lighter)
  'Social':     '#e9c46a',   // Saffron
  'Review':     '#f4a261',   // Sandy Brown
  'Directory':  '#B95F51',   // Burnt Sienna (darker)
  'Other':      '#d1d5db'    // Neutral gray
};
const ENGINE_COLORS = {
  chatgpt:    '#10a37f',
  claude:     '#cc785c',
  gemini:     '#4285f4',
  perplexity: '#1fb8cd'
};
const ENGINE_LABELS = {
  chatgpt:    'ChatGPT',
  claude:     'Claude',
  gemini:     'Gemini',
  perplexity: 'Perplexity'
};
const BRAND = {
  jetBlack: '#0A0908',
  teal:     '#048ba8',
  tealBg:   'rgba(4,139,168,0.08)',
  textLight:'#6b7280',
  border:   '#e5e7eb',
  bg:       '#f9fafb'
};
const font = 'Work Sans, -apple-system, BlinkMacSystemFont, sans-serif';

// ── CSV Download Helper ────────────────────────────────────────────
function downloadCSV(filename, rows) {
  const csv = rows.map(r => r.map(cell => {
    const str = String(cell || '');
    return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
  }).join(',')).join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// ── Reusable sub-components ───────────────────────────────────────
function Card({ children, style = {} }) {
  return (
    <div style={{
      background: 'white',
      borderRadius: 12,
      border: `1px solid ${BRAND.border}`,
      padding: 24,
      ...style
    }}>
      {children}
    </div>
  );
}
function SectionLabel({ children }) {
  return (
    <p style={{
      fontSize: 11,
      fontWeight: 700,
      color: BRAND.textLight,
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      marginBottom: 4
    }}>
      {children}
    </p>
  );
}
function SectionTitle({ children }) {
  return (
    <p style={{ fontSize: 16, fontWeight: 700, color: BRAND.jetBlack, marginBottom: 4 }}>
      {children}
    </p>
  );
}
function SectionSub({ children }) {
  return (
    <p style={{ fontSize: 13, color: BRAND.textLight, marginBottom: 20, lineHeight: 1.5 }}>
      {children}
    </p>
  );
}
// Horizontal percentage bar
function Bar({ pct, color, height = 10 }) {
  return (
    <div style={{
      background: BRAND.bg,
      borderRadius: height,
      height,
      width: '100%',
      overflow: 'hidden'
    }}>
      <div style={{
        background: color,
        height,
        width: `${Math.max(pct, 0.5)}%`,
        borderRadius: height,
        transition: 'width 0.5s ease'
      }} />
    </div>
  );
}
// Colour swatch dot
function Dot({ color, size = 10 }) {
  return (
    <span style={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: 3,
      background: color,
      flexShrink: 0
    }} />
  );
}
// ── Main component ────────────────────────────────────────────────
function SourceAnalysisDashboard({ campaignId, apiBaseUrl }) {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);
  useEffect(() => {
    if (!campaignId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    fetch(`${apiBaseUrl}/modules/citations/${campaignId}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [campaignId, apiBaseUrl]);
  // ── Loading ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 64, fontFamily: font }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 36,
            height: 36,
            border: `3px solid ${BRAND.teal}`,
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'sa-spin 0.8s linear infinite',
            margin: '0 auto 12px'
          }} />
          <p style={{ color: BRAND.textLight, fontSize: 14 }}>Loading source analysis…</p>
        </div>
        <style>{`@keyframes sa-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  // ── Error ────────────────────────────────────────────────────────
  if (error) {
    return (
      <div style={{
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 12,
        padding: 24,
        fontFamily: font
      }}>
        <p style={{ color: '#dc2626', fontSize: 14 }}>⚠️ Failed to load citation analysis: {error}</p>
      </div>
    );
  }
  // ── No data ──────────────────────────────────────────────────────
  if (!data || data.auditsAnalyzed === 0) {
    return (
      <Card style={{ fontFamily: font, textAlign: 'center', padding: 48 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>📊</div>
        <p style={{ fontSize: 16, fontWeight: 700, color: BRAND.jetBlack, marginBottom: 8 }}>
          No Citation Data Yet
        </p>
        <p style={{ fontSize: 14, color: BRAND.textLight }}>
          Run an LLM audit to see source type analysis.
        </p>
      </Card>
    );
  }
  const {
    byCategory, byCategoryEngine, byEngine,
    competitorShare, timeSeries, topDomains,
    totalCitations, classifiedPct, otherPct,
    dateRange, auditsAnalyzed
  } = data;
  // Pivot byCategoryEngine → { engine → { category → count } }
  const engineBreakdown = {};
  for (const item of byCategoryEngine) {
    if (!engineBreakdown[item.engine]) engineBreakdown[item.engine] = {};
    engineBreakdown[item.engine][item.category] = item.count;
  }
  // Max count for byCategory relative bars
  const maxCatCount = Math.max(...byCategory.map(b => b.count), 1);
  // Max total for time series bars
  const maxTimeTotal = Math.max(...timeSeries.map(t => t.total), 1);
  // Whether any real competitor data exists (not just "Third-party")
  const hasCompetitors = competitorShare.some(b => !b.isTarget && !b.isThirdParty);

  // Download top domains table to CSV
  const downloadTopDomainsTableCSV = () => {
    const today = new Date().toISOString().split('T')[0];
    const rows = [
      ['Domain', 'Category', 'Citations', 'Share %'],
      ...(topDomains || []).map(({ domain, count, sharePct, category }) => [
        domain,
        category,
        count.toString(),
        sharePct.toString()
      ])
    ];
    downloadCSV(`top-cited-sources-${today}.csv`, rows);
  };

  return (
    <div style={{ fontFamily: font }}>
      {/* ── Page header ──────────────────────────────────────────── */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: BRAND.jetBlack, marginBottom: 4 }}>
          Source Analysis
        </h2>
        <p style={{ fontSize: 13, color: BRAND.textLight }}>
          {auditsAnalyzed} audit{auditsAnalyzed !== 1 ? 's' : ''} analysed
          &nbsp;·&nbsp; {totalCitations} total citations
          &nbsp;·&nbsp; {dateRange.from} → {dateRange.to}
        </p>
      </div>
      {/* ── Top Cited Sources Table ─────────────────────────────── */}
      {topDomains && topDomains.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <SectionLabel>Top Cited Sources</SectionLabel>
              <SectionTitle>Most Frequently Cited Domains</SectionTitle>
              <SectionSub>
                All {topDomains.length} domains ranked by citation count across all audit runs
              </SectionSub>
            </div>
            <button
              onClick={downloadTopDomainsTableCSV}
              title="Download as CSV"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '0',
                fontSize: '12px',
                color: '#6b7280',
                transition: 'color 0.2s',
                flexShrink: 0,
                marginLeft: 16
              }}
              onMouseEnter={e => e.currentTarget.style.color = BRAND.teal}
              onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
            >
              <i className="ti ti-download" style={{ fontSize: '14px' }} />
              <span>CSV</span>
            </button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${BRAND.border}` }}>
                  {[
                    { label: 'Domain', align: 'left' },
                    { label: 'Category', align: 'left' },
                    { label: 'Engines', align: 'left' },
                    { label: 'Citations', align: 'right' },
                    { label: 'Share', align: 'right' }
                  ].map(h => (
                    <th key={h.label} style={{
                      padding: '8px 12px',
                      textAlign: h.align,
                      fontSize: 11,
                      fontWeight: 700,
                      color: BRAND.textLight,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      whiteSpace: 'nowrap'
                    }}>
                      {h.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topDomains.map(({ domain, count, sharePct, category, isOwned, engines: domEngines }, i) => {
                  const catColor = CATEGORY_COLORS[category] || '#9ca3af';
                  return (
                    <tr
                      key={domain}
                      style={{
                        borderBottom: `1px solid ${BRAND.border}`,
                        background: i % 2 === 0 ? 'white' : BRAND.bg
                      }}
                    >
                      {/* Domain */}
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 12, color: '#9ca3af', minWidth: 20, fontWeight: 600 }}>
                            {i + 1}
                          </span>
                          <img
                            src={`https://www.google.com/s2/favicons?domain=${domain}&sz=32`}
                            alt=""
                            width={16}
                            height={16}
                            style={{ borderRadius: 3, flexShrink: 0 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                          <span style={{ fontWeight: 600, color: BRAND.jetBlack }}>{domain}</span>
                          {isOwned && (
                            <span style={{
                              fontSize: 10, fontWeight: 700, padding: '1px 6px',
                              borderRadius: 999, background: 'rgba(4,139,168,0.12)', color: BRAND.teal
                            }}>
                              owned
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Category */}
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          fontSize: 12, fontWeight: 600, padding: '2px 8px',
                          borderRadius: 999, background: catColor + '18', color: catColor
                        }}>
                          <Dot color={catColor} size={7} />
                          {category}
                        </span>
                      </td>
                      {/* Engines */}
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          {(domEngines || []).map(eng => (
                            <span
                              key={eng}
                              title={ENGINE_LABELS[eng] || eng}
                              style={{
                                width: 10, height: 10, borderRadius: '50%',
                                background: ENGINE_COLORS[eng] || '#9ca3af',
                                display: 'inline-block', flexShrink: 0
                              }}
                            />
                          ))}
                        </div>
                      </td>
                      {/* Citations */}
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 700, color: BRAND.jetBlack }}>
                        {count}
                      </td>
                      {/* Share */}
                      <td style={{ padding: '10px 12px', textAlign: 'right', color: BRAND.textLight }}>
                        {sharePct}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Engine legend */}
          <div style={{
            display: 'flex', gap: 16, marginTop: 16,
            paddingTop: 12, borderTop: `1px solid ${BRAND.border}`
          }}>
            {Object.entries(ENGINE_LABELS).map(([eng, label]) => (
              <span key={eng} style={{ fontSize: 11, color: BRAND.textLight, display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: ENGINE_COLORS[eng], display: 'inline-block' }} />
                {label}
              </span>
            ))}
          </div>
        </Card>
      )}
      {/* ── Row 1: Publisher Types + Citation Share ───────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 16,
        marginBottom: 16
      }}>
        {/* Chart 2: Publisher Type Breakdown */}
        <Card>
          <SectionLabel>Publisher Type Breakdown</SectionLabel>
          <SectionTitle>Source Categories</SectionTitle>
          <SectionSub>
            {classifiedPct}% classified by type &bull; {otherPct}% Other
            (corporate, brand &amp; unrecognised domains)
          </SectionSub>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {byCategory.map(({ category, count }) => {
              const pct = Math.round((count / totalCitations) * 100);
              const barPct = Math.round((count / maxCatCount) * 100);
              const color = CATEGORY_COLORS[category] || '#9ca3af';
              return (
                <div key={category}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 6
                  }}>
                    <span style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: BRAND.jetBlack,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <Dot color={color} />
                      {category}
                    </span>
                    <span style={{ fontSize: 13, color: BRAND.textLight }}>
                      {count}&nbsp;
                      <span style={{ color: '#9ca3af' }}>({pct}%)</span>
                    </span>
                  </div>
                  <Bar pct={barPct} color={color} />
                </div>
              );
            })}
          </div>
          <p style={{
            fontSize: 11,
            color: '#9ca3af',
            marginTop: 20,
            lineHeight: 1.6,
            padding: '12px',
            background: BRAND.bg,
            borderRadius: 8
          }}>
            Classified categories: News, Government, Academic, Reference, Social,
            Review, Directory. "Other" covers unclassified corporate and brand sites —
            common in niche industries where specialist publishers aren't in the taxonomy yet.
          </p>
        </Card>
        {/* Chart 4: Citation Share */}
        <Card>
          <SectionLabel>Citation Share</SectionLabel>
          <SectionTitle>Who Gets Cited</SectionTitle>
          <SectionSub>
            Distribution of citations across your brand, competitors,
            and third-party sources
          </SectionSub>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {competitorShare.map(({ brand, isTarget, isThirdParty, citations, sharePct, citationScore }) => {
              const color = isTarget
                ? BRAND.teal
                : isThirdParty
                  ? '#9ca3af'
                  : '#f4a261';
              const icon = isTarget ? '🎯' : isThirdParty ? '🌐' : '🏢';
              const label = isTarget
                ? `${brand} (your brand)`
                : isThirdParty
                  ? 'Third-party sources'
                  : brand;
              return (
                <div key={brand + (isThirdParty ? '-tp' : '')}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 6
                  }}>
                    <span style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: BRAND.jetBlack
                    }}>
                      {icon}&nbsp;{label}
                    </span>
                    <span style={{ fontSize: 13, color: BRAND.textLight, textAlign: 'right' }}>
                      {citations}&nbsp;
                      <span style={{ color: '#9ca3af' }}>({sharePct}%)</span>
                    </span>
                  </div>
                  <Bar pct={sharePct} color={color} height={14} />
                  {isTarget && typeof citationScore === 'number' && citationScore > 0 && (
                    <p style={{ fontSize: 11, color: BRAND.teal, marginTop: 4 }}>
                      Citation rate: {citationScore}%
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          {!hasCompetitors && (
            <p style={{
              fontSize: 12,
              color: '#9ca3af',
              marginTop: 20,
              lineHeight: 1.6,
              padding: 12,
              background: BRAND.bg,
              borderRadius: 8
            }}>
              No competitor citations tracked yet. Add competitors to the campaign
              to compare citation share.
            </p>
          )}
        </Card>
      </div>
      {/* ── Chart 3: Sources by Engine ───────────────────────────── */}
      <Card style={{ marginBottom: 16 }}>
        <SectionLabel>Per-Engine Breakdown</SectionLabel>
        <SectionTitle>Source Types by Engine</SectionTitle>
        <SectionSub>
          How each AI engine's citations break down by publisher category —
          hover a segment for the count
        </SectionSub>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {byEngine.map(({ engine, totalCitations: engineTotal }) => {
            const cats = engineBreakdown[engine] || {};
            const engineColor = ENGINE_COLORS[engine] || BRAND.teal;
            const label = ENGINE_LABELS[engine] || engine;
            const sorted = Object.entries(cats).sort(([, a], [, b]) => b - a);
            return (
              <div key={engine}>
                {/* Engine label row */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 8
                }}>
                  <span style={{
                    display: 'inline-block',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: engineColor,
                    flexShrink: 0
                  }} />
                  <span style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: engineColor,
                    minWidth: 90
                  }}>
                    {label}
                  </span>
                  <span style={{ fontSize: 12, color: BRAND.textLight }}>
                    {engineTotal} citation{engineTotal !== 1 ? 's' : ''}
                  </span>
                </div>
                {/* Stacked bar */}
                <div style={{
                  display: 'flex',
                  height: 22,
                  borderRadius: 6,
                  overflow: 'hidden',
                  background: BRAND.bg
                }}>
                  {sorted.map(([category, count]) => {
                    const segPct = (count / engineTotal) * 100;
                    const color = CATEGORY_COLORS[category] || '#9ca3af';
                    return (
                      <div
                        key={category}
                        title={`${category}: ${count} (${Math.round(segPct)}%)`}
                        style={{
                          width: `${segPct}%`,
                          background: color,
                          transition: 'width 0.5s ease',
                          minWidth: count > 0 ? 2 : 0
                        }}
                      />
                    );
                  })}
                </div>
                {/* Mini legend */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px 14px',
                  marginTop: 6
                }}>
                  {sorted.map(([category, count]) => (
                    <span key={category} style={{
                      fontSize: 11,
                      color: BRAND.textLight,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4
                    }}>
                      <Dot color={CATEGORY_COLORS[category] || '#9ca3af'} size={8} />
                      {category} {count}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      {/* ── Chart 1: Citation Volume Over Time ───────────────────── */}
      {timeSeries.length > 0 && (
        <Card>
          <SectionLabel>Citation Trend</SectionLabel>
          <SectionTitle>Source Volume Over Time</SectionTitle>
          <SectionSub>
            Total citations per audit run, broken down by publisher type
          </SectionSub>
          {/* Bars */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 12,
            height: 160,
            paddingBottom: 8
          }}>
            {timeSeries.map(({ date, categories, total, ownedCount }) => {
              const barPct = (total / maxTimeTotal) * 100;
              const barHeightPx = Math.round((barPct / 100) * 140);
              const sorted = Object.entries(categories).sort(([, a], [, b]) => b - a);
              return (
                <div key={date + total} style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  height: '100%'
                }}>
                  {/* Citation count label */}
                  <span style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: BRAND.jetBlack,
                    marginBottom: 4
                  }}>
                    {total}
                  </span>
                  {/* Stacked bar column */}
                  <div style={{
                    width: '80%',
                    height: barHeightPx,
                    borderRadius: '6px 6px 0 0',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 4
                  }}>
                    {sorted.map(([cat, count]) => {
                      const segPct = (count / total) * 100;
                      return (
                        <div
                          key={cat}
                          title={`${cat}: ${count}`}
                          style={{
                            width: '100%',
                            height: `${segPct}%`,
                            background: CATEGORY_COLORS[cat] || '#9ca3af',
                            minHeight: count > 0 ? 2 : 0
                          }}
                        />
                      );
                    })}
                  </div>
                  {/* X-axis */}
                  <div style={{
                    borderTop: `1px solid ${BRAND.border}`,
                    width: '100%',
                    marginTop: 4
                  }} />
                  {/* Date label */}
                  <span style={{
                    fontSize: 11,
                    color: BRAND.textLight,
                    marginTop: 4,
                    textAlign: 'center'
                  }}>
                    {date.slice(5)} {/* MM-DD */}
                  </span>
                  {/* Owned count indicator */}
                  {ownedCount > 0 && (
                    <span style={{
                      fontSize: 10,
                      color: BRAND.teal,
                      fontWeight: 600,
                      marginTop: 2
                    }}>
                      {ownedCount} owned
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {/* Category colour legend */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '6px 16px',
            marginTop: 16,
            paddingTop: 12,
            borderTop: `1px solid ${BRAND.border}`
          }}>
            {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
              <span key={cat} style={{
                fontSize: 11,
                color: BRAND.textLight,
                display: 'flex',
                alignItems: 'center',
                gap: 4
              }}>
                <Dot color={color} size={8} />
                {cat}
              </span>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
export default SourceAnalysisDashboard;
